import { Avatar, Callout } from '@assembly-js/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { renderText } from 'stream-chat-react';
import { makeStyles } from 'tss-react/mui';

import { APP_SETUP_PAGE, PERM_MOD_SETTINGS_MESSAGING } from 'src/constants';
import { useAppSelector } from 'src/hooks/useStore';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { HoverBackground, NonHoverBorder } from 'src/theme/colors';
import { getInitials, hasPermission, UserProfile } from 'src/utils/UserUtils';

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    border: `1px solid ${NonHoverBorder}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
  },
  instructionWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    padding: theme.spacing(1.5),
    width: '100%',
    backgroundColor: HoverBackground,
    borderRadius: theme.shape.borderRadius,
  },
  instructionTextWrapper: {
    marginLeft: theme.spacing(2),
  },
  infoIcon: {
    height: 14,
    width: 14,
    marginTop: '5px',
  },
  placeholderMessageWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: theme.spacing(3),
  },
  placeholderMessageBody: {
    marginLeft: theme.spacing(1.5),
    marginTop: theme.spacing(-0.5),
    '& ol, & ul': {
      listStylePosition: 'inside',
    },
  },
}));

interface ChannelPlaceholderMessageProps {
  messageBody: string;
  messageAuthor?: UserProfile;
}

export const ChannelPlaceholderMessage: React.FC<
  ChannelPlaceholderMessageProps
> = ({ messageBody, messageAuthor }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { permissions } = useAppSelector((state) => ({
    permissions: state.user.data?.permissions,
  }));

  const hasSettingsAccess = hasPermission(
    PERM_MOD_SETTINGS_MESSAGING,
    permissions,
  );
  return (
    <div className={classes.wrapper}>
      <Callout
        variant="info"
        description={t(($) =>
          hasSettingsAccess
            ? $.messages.welcome_callout.description_with_settings_access
            : $.messages.welcome_callout.description_without_settings_access,
        )}
        {...(hasSettingsAccess
          ? {
              footerAction: {
                children: 'Update settings',
                className: 'text-base',
                href: `${APP_SETUP_PAGE.path}/setup?moduleType=module&moduleId=messages`,
              },
            }
          : {})}
      />
      <ul className="str-chat__ul">
        <li className="str_chat__li">
          <div className="str-chat__message-team">
            <div className={classes.placeholderMessageWrapper}>
              <Avatar
                size="lg"
                src={messageAuthor?.avatarUrl ?? ''}
                fallbackColor={messageAuthor?.fallbackColor}
                variant="circle"
                text={getInitials(
                  `${messageAuthor?.firstName ?? ''} ${messageAuthor?.lastName ?? ''}`,
                  {
                    twoLetters: true,
                  },
                )}
              />
              <div className={classes.placeholderMessageBody}>
                <BaseTypography fontType="15Medium">{`${messageAuthor?.firstName} ${messageAuthor?.lastName}`}</BaseTypography>
                <div
                  className="str-chat__message-team-content"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: 0,
                    width: '100%',
                  }} // override css specificity for message-team-content
                >
                  {renderText(messageBody)}
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  );
};
